import { Users, Settings, TrendingUp, CheckCircle, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ScrollReveal from "@/components/ScrollReveal";

const ProcessSection = () => {
  const processSteps = [
    {
      number: "01",
      title: "We Recruit & Build Your Team",
      description: "Hand-picked content specialists who understand your industry, voice, and vision—permanently on your payroll.",
      icon: Users,
      color: "primary",
      delay: 200
    },
    {
      number: "02", 
      title: "We Install the Operating System",
      description: "Custom workflows, brand guidelines, content calendars, and performance tracking systems that scale with your business.",
      icon: Settings,
      color: "accent",
      delay: 300
    },
    {
      number: "03",
      title: "We Train for Peak Performance", 
      description: "90 days of intensive training and optimization until your team operates like a world-class content machine.",
      icon: TrendingUp,
      color: "primary",
      delay: 400
    }
  ];

  return (
    <section className="section-spacing bg-gradient-to-b from-primary/5 to-background">
      <div className="container mx-auto px-4 lg:px-8">
        <ScrollReveal delay={100}>
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 glass-panel rounded-full border border-accent/20">
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-accent">Our Process</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-display font-bold mb-6 lg:mb-8 leading-tight text-foreground">
              We Don't Run Your Content.{" "}
              <br className="hidden sm:block" />
              <span className="bg-gradient-brand bg-clip-text text-transparent">
                We Build Your Content Muscle.
              </span>
            </h2>
            
            <p className="text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              Transform from content dependency to content ownership in just 90 days
            </p>
          </div>
        </ScrollReveal>

        {/* Process Steps */}
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 mb-16">
            {processSteps.map((step, index) => (
              <ScrollReveal key={index} delay={step.delay}>
                <div className="relative group">
                  {/* Connection line for desktop */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-16 left-full w-12 h-0.5 bg-gradient-to-r from-primary/40 to-accent/40 z-0" />
                  )}
                  
                  <div className="relative glass-panel p-6 lg:p-8 rounded-2xl border border-primary/20 hover:border-primary/40 transition-all duration-300 hover:-translate-y-2 h-full">
                    {/* Background glow */}
                    <div className={`absolute inset-0 ${step.color === 'green' ? 'bg-gradient-to-br from-green-500/5 to-green-400/10' : `bg-gradient-to-br from-${step.color}/5 to-${step.color}/10`} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                    
                    {/* Step number */}
                    <div className="relative z-10">
                      <div className="flex items-center gap-4 mb-6">
                        <div className={`w-12 h-12 rounded-full ${step.color === 'green' ? 'bg-green-500/10 border border-green-500/20' : `bg-${step.color}/10 border border-${step.color}/20`} flex items-center justify-center`}>
                          <step.icon className={`w-6 h-6 ${step.color === 'green' ? 'text-green-500' : `text-${step.color}`}`} />
                        </div>
                        <div className={`text-3xl font-display font-bold ${step.color === 'green' ? 'text-green-500/60' : `text-${step.color}/60`}`}>
                          {step.number}
                        </div>
                      </div>
                      
                      <h3 className="text-xl lg:text-2xl font-display font-bold mb-4 text-foreground leading-tight">
                        {step.title}
                      </h3>
                      
                      <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Team visualization */}
          <ScrollReveal delay={500}>
            <div className="text-center">
              <div className="glass-panel p-6 lg:p-8 rounded-2xl border border-accent/20 max-w-2xl mx-auto">
                {/* Team avatars */}
                <div className="flex justify-center items-center gap-4 mb-6">
                  <div className="flex -space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent border-2 border-background flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent to-secondary border-2 border-background flex items-center justify-center">
                      <Settings className="w-6 h-6 text-white" />
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-green-500 border-2 border-background flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-500/10 rounded-full">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-green-500">Active</span>
                  </div>
                </div>
                
                <div className="text-lg font-semibold text-foreground mb-2">
                  10+ teams already built and deployed
                </div>
                <div className="text-sm text-muted-foreground">
                  Ready to create your content powerhouse?
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Bottom highlight */}
        <ScrollReveal delay={600}>
          <div className="mt-16 lg:mt-20 text-center">
            <div className="max-w-4xl mx-auto p-6 lg:p-8 glass-panel rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
              <h4 className="text-xl lg:text-2xl font-display font-bold mb-4 text-foreground">
                Ready to Build Your Content Empire?
              </h4>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed mb-6">
                From zero to content independence in 90 days. Your team, your systems, your success.
              </p>
              <Button 
                variant="hero" 
                size="lg" 
                className="text-sm lg:text-base min-h-[48px] lg:min-h-[56px] font-semibold group"
                onClick={() => window.location.href = '/contact'}
              >
                Start Building My Team
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ProcessSection;